import { compatibleProviderContinuation } from "@cleo/shared/claudeAccountContinuation";
import {
  MANAGED_PROVIDER_INSTANCE_ID,
  type ProviderInstanceId,
  type ProviderDriverKind,
  type ResolvedKeybindingsConfig,
  type ModelCatalog,
} from "@cleo/contracts";
import { resolveSelectableModel } from "@cleo/shared/model";
import { engineSwitchNotice } from "@cleo/shared/providerSwitchLaw";
import { LegendList, type LegendListRef } from "@legendapp/list/react";
import { useRouter } from "@tanstack/react-router";
import {
  Fragment,
  memo,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from "lucide-react";
import { NeucleosTriangle } from "../NeucleosTriangle";
import { ModelListRow } from "./ModelListRow";
import { ManageModelsButton } from "../local-models/ManageModelsButton";
import {
  DrilldownDoorControl,
  ModelPickerAccountsPage,
  ModelPickerProvidersPage,
  type DrilldownDoor,
} from "./ModelPickerDrilldownPages";
import { openProviderSettings, verifyNativeClaudeAccount } from "./providerRecoveryActions";
import { useModelListReveal } from "./modelListReveal";
import {
  groupModelPickerProviders,
  modelPickerBackTarget,
  modelPickerBreadcrumb,
  modelPickerPageState,
  resolveModelPickerLanding,
  resolveProviderOpenTarget,
  type ModelPickerPageIndex,
  type ModelPickerProviderGroup,
  type ModelPickerRoot,
  type ModelPickerView,
} from "./modelPickerPages";
import {
  modelPickerLegacySectionKey,
  modelPickerModelKey,
  parseModelPickerLegacySectionKey,
  parseModelPickerModelKey,
} from "./modelPickerKeys";
import { isModelPickerNewModel } from "./modelPickerModelHighlights";
import {
  modelNeedsProviderVersionDoorLabel,
  modelNeedsProviderVersionNote,
  providerCliName,
} from "./modelProviderVersionGate";
import { buildModelPickerSearchText, scoreModelPickerSearch } from "./modelPickerSearch";
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxListVirtualized,
} from "../ui/combobox";
import { ModelEsque } from "./providerIconUtils";
import {
  modelPickerJumpCommandForIndex,
  modelPickerJumpIndexFromCommand,
  resolveShortcutCommand,
  shortcutLabelForCommand,
} from "../../keybindings";
import { getGovernedProviderDisabledReason } from "../ChatView.logic";
import { useClientSettings, useUpdateClientSettings } from "~/hooks/useSettings";
import { cn } from "~/lib/utils";
import { getVirtualizedScrollFadeClassName } from "../ui/scroll-area";
import { TooltipProvider } from "../ui/tooltip";
import {
  isProviderInstancePickerReady,
  isProviderInstancePickerVisible,
  providerInstanceRecovery,
  providerInstanceStatusNote,
  type ProviderInstanceEntry,
} from "../../providerInstances";
import { providerModelKey, sortProviderModelItems } from "../../modelOrdering";
import {
  MANAGED_CATALOG_LOADING,
  resolveModelChooserRoot,
  type ManagedChooserRow,
} from "./modelChooserRoot";
import { MANAGED_IMAGES_UNREADABLE, managedSlugReadsImages } from "./managedImageInput";

type ModelPickerItem = {
  slug: string;
  name: string;
  shortName?: string;
  subProvider?: string;
  instanceId: ProviderInstanceId;
  driverKind: ProviderDriverKind;
  instanceDisplayName: string;
  instanceAccentColor?: string | undefined;
  continuationGroupKey?: string | undefined;
  isLegacy?: boolean | undefined;
  /** Shown, not choosable: the provider CLI must reach this version first. */
  needsProviderVersion?: string | undefined;
};

const EMPTY_MODEL_JUMP_LABELS = new Map<string, string>();
const EMPTY_ITEM_KEYS: string[] = [];

type ClaudeVerifyProgress = "checking" | "verified" | "not-verified";

/** F36: why an instance that is not ready cannot be chosen; null when it is ready. */
function readinessReason(entry: ProviderInstanceEntry): string | null {
  return isProviderInstancePickerReady(entry)
    ? null
    : (providerInstanceStatusNote(entry) ?? "Not ready");
}

function ModelListSeparator() {
  return <div className="h-0.5" />;
}

/**
 * True when the entry is the engine a started conversation is bound to (its
 * driver, and a continuation compatible with the bound account), or when no
 * engine is bound. Since Can 21 (2026-10-02) this decides only whether a row
 * carries the switch notice; every engine stays choosable.
 */
export function matchesModelPickerContinuation(
  entry: Pick<ProviderInstanceEntry, "driverKind" | "continuationGroupKey">,
  lockedProvider: ProviderDriverKind | null,
  lockedKey: string | null | undefined,
): boolean {
  if (lockedProvider === null) return true;
  if (entry.driverKind !== lockedProvider) return false;
  return !lockedKey || compatibleProviderContinuation(entry.continuationGroupKey, lockedKey);
}

export const ModelPickerContent = memo(function ModelPickerContent(props: {
  /** The instance currently selected in the composer (combobox "value"). */
  activeInstanceId: ProviderInstanceId;
  model: string;
  worldKey?: string | null;
  managedCatalog?: ModelCatalog | null;
  managedCatalogRefusal?: string | null;
  /** The World's catalog is being read: say so, never "unavailable", until it answers. */
  managedCatalogLoading?: boolean;
  /**
   * Images this conversation holds, with the message being written. A managed
   * row whose model the World says cannot read images is marked (W-a8c32a52).
   */
  conversationImageCount?: number;
  /**
   * The engine a started conversation is bound to (its driver kind), or null.
   * Until Can 21 (2026-10-02) this locked the picker to that driver; now every
   * engine stays choosable and the rows of another engine carry the notice
   * "The conversation so far will be given to <label>." The prop keeps its
   * name so the pickers that pass it need no change.
   */
  lockedProvider: ProviderDriverKind | null;
  lockedContinuationGroupKey?: string | null;
  /**
   * True when the composer thread is governed — its next send carries a
   * frozen contextResolution, which only drivers with a grant harness can
   * record. Providers on harness-less drivers are disabled with a human
   * reason; the server refuses them fail-closed either way.
   */
  governedContext?: boolean;
  /**
   * All configured provider instances in display order. Used to render
   * the sidebar (one button per instance) and to resolve display names
   * for the locked-mode header.
   */
  instanceEntries: ReadonlyArray<ProviderInstanceEntry>;
  keybindings?: ResolvedKeybindingsConfig;
  /**
   * Model options per instance. Keyed by `ProviderInstanceId` so the
   * default Codex instance and any custom Codex instances each have their
   * own list (custom instances typically start with the same built-in
   * model set but are free to diverge via customModels).
   */
  modelOptionsByInstance: ReadonlyMap<ProviderInstanceId, ReadonlyArray<ModelEsque>>;
  terminalOpen: boolean;
  onRequestClose?: () => void;
  getModelDisabledReason?: (instanceId: ProviderInstanceId, model: string) => string | null;
  onInstanceModelChange: (instanceId: ProviderInstanceId, model: string) => void;
  /**
   * The D1 door, retired with the lock (Can 21, 2026-10-02): another engine is
   * now chosen in place, so the picker offers no new-session door. Accepted so
   * the pickers that still pass it need no change.
   */
  onStartSessionWithProvider?: (instanceId: ProviderInstanceId, model: string) => void;
}) {
  const {
    keybindings: providedKeybindings,
    modelOptionsByInstance,
    instanceEntries,
    getModelDisabledReason,
    onInstanceModelChange,
  } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [showTopScrollFade, setShowTopScrollFade] = useState(false);
  const [showBottomScrollFade, setShowBottomScrollFade] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const modelListRef = useRef<LegendListRef | null>(null);
  const highlightedModelKeyRef = useRef<string | null>(null);
  const favorites = useClientSettings((s) => s.favorites ?? []);
  const [initialLanding] = useState(() =>
    resolveModelPickerLanding({
      activeInstanceId: props.activeInstanceId,
      hasFavorites: favorites.length > 0,
      locked: props.lockedProvider !== null,
      entries: instanceEntries,
    }),
  );
  const [selectedInstanceId, setSelectedInstanceId] = useState<ModelPickerRoot>(
    initialLanding.root,
  );
  const [view, setView] = useState<ModelPickerView>(initialLanding.view);
  const [expandedLegacyInstances, setExpandedLegacyInstances] = useState(
    () =>
      new Set<ProviderInstanceId>(
        modelOptionsByInstance
          .get(props.activeInstanceId)
          ?.some((model) => model.slug === props.model && model.isLegacy)
          ? [props.activeInstanceId]
          : [],
      ),
  );
  const keybindings = useMemo<ResolvedKeybindingsConfig>(
    () => providedKeybindings ?? [],
    [providedKeybindings],
  );
  const updateSettings = useUpdateClientSettings();
  const groupedRoot = useMemo(
    () => resolveModelChooserRoot(instanceEntries, props.managedCatalog ?? null),
    [instanceEntries, props.managedCatalog],
  );

  const focusSearchInput = useCallback(() => {
    searchInputRef.current?.focus({ preventScroll: true });
  }, []);

  const handleSelectInstance = useCallback(
    (instanceId: ModelPickerRoot) => {
      setSelectedInstanceId(instanceId);
      setView(
        instanceId === "favorites"
          ? { page: 2, providerKind: null }
          : {
              page: 2,
              providerKind:
                instanceEntries.find((entry) => entry.instanceId === instanceId)?.driverKind ??
                null,
            },
      );
      setSearchQuery("");
      window.requestAnimationFrame(() => {
        focusSearchInput();
      });
    },
    [focusSearchInput, instanceEntries],
  );

  useLayoutEffect(() => {
    focusSearchInput();
    const frame = window.requestAnimationFrame(() => {
      focusSearchInput();
    });
    const timeout = window.setTimeout(() => {
      focusSearchInput();
    }, 0);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [focusSearchInput]);

  // Create a Set for efficient lookup. Favorites are keyed by
  // `${instanceId}:${slug}`; the storage schema widened from ProviderDriverKind
  // to ProviderInstanceId so pre-migration favorites keyed by driver slugs
  // (e.g. `"codex:gpt-5"`) still resolve — the default instance id equals
  // the driver slug.
  const favoritesSet = useMemo(() => {
    return new Set(favorites.map((fav) => providerModelKey(fav.provider, fav.model)));
  }, [favorites]);

  /**
   * Lookup table keyed by `instanceId`. Used for display name + driver
   * kind enrichment and for `ready`/enabled filtering before flattening
   * models into the search list.
   */
  const entryByInstanceId = useMemo(
    () => new Map(instanceEntries.map((entry) => [entry.instanceId, entry])),
    [instanceEntries],
  );
  const matchesBoundEngine = useCallback(
    (entry: Pick<ProviderInstanceEntry, "driverKind" | "continuationGroupKey">): boolean => {
      return matchesModelPickerContinuation(
        entry,
        props.lockedProvider,
        props.lockedContinuationGroupKey,
      );
    },
    [props.lockedContinuationGroupKey, props.lockedProvider],
  );

  /**
   * PKR1 / F36: two sets, one law each. `visibleInstanceSet` is what the person
   * can see — every configured instance this device can run and this World may
   * show, whatever its readiness. `readyInstanceSet` is what this thread may
   * choose — the visible set narrowed by readiness and the governed-harness
   * rule (the D1 lock left this set with Can 21, 2026-10-02). A visible-but-unchoosable instance renders greyed
   * with its one-sentence reason and, when it is not ready, the door that
   * fixes it; it is never silently removed.
   */
  const visibleInstanceSet = useMemo(() => {
    const visible = new Set<ProviderInstanceId>();
    for (const entry of instanceEntries) {
      if (
        isProviderInstancePickerVisible(entry) &&
        (!entry.snapshot.connectionWorldKey || entry.snapshot.connectionWorldKey === props.worldKey)
      ) {
        visible.add(entry.instanceId);
      }
    }
    return visible;
  }, [instanceEntries, props.worldKey]);
  const readyInstanceSet = useMemo(() => {
    const ready = new Set<ProviderInstanceId>();
    for (const entry of instanceEntries) {
      if (
        visibleInstanceSet.has(entry.instanceId) &&
        isProviderInstancePickerReady(entry) &&
        !getGovernedProviderDisabledReason({
          governed: props.governedContext === true,
          driverKind: entry.driverKind,
          label: entry.displayName,
        })
      ) {
        ready.add(entry.instanceId);
      }
    }
    return ready;
  }, [instanceEntries, props.governedContext, visibleInstanceSet]);

  // Flatten models into a searchable array. One pass over the
  // instance-keyed map; each model carries its instance id + driver kind
  // so the list row can render the right icon and display name without
  // another lookup.
  const flatModels = useMemo(() => {
    const out: ModelPickerItem[] = [];
    for (const [instanceId, models] of modelOptionsByInstance) {
      const entry = entryByInstanceId.get(instanceId);
      if (!entry) {
        // Instance disappeared between renders (configuration change). Skip
        // its models — stale options shouldn't appear in the picker.
        continue;
      }
      if (!readyInstanceSet.has(instanceId)) {
        continue;
      }
      for (const model of models) {
        if (getModelDisabledReason?.(instanceId, model.slug)) continue;
        if (
          instanceId === "neucleos" &&
          ![...groupedRoot.neucleos, ...groupedRoot.frontier].some((row) => row.slug === model.slug)
        )
          continue;
        out.push({
          slug: model.slug,
          name: model.name,
          ...(model.shortName ? { shortName: model.shortName } : {}),
          ...(model.subProvider ? { subProvider: model.subProvider } : {}),
          ...(model.isLegacy ? { isLegacy: true } : {}),
          ...(model.needsProviderVersion
            ? { needsProviderVersion: model.needsProviderVersion }
            : {}),
          instanceId,
          driverKind: entry.driverKind,
          instanceDisplayName: entry.displayName,
          ...(entry.accentColor ? { instanceAccentColor: entry.accentColor } : {}),
          ...(entry.continuationGroupKey
            ? { continuationGroupKey: entry.continuationGroupKey }
            : {}),
        });
      }
    }
    for (const row of [...groupedRoot.neucleos, ...groupedRoot.frontier]) {
      const entry = entryByInstanceId.get(row.instanceId);
      if (
        !entry ||
        !readyInstanceSet.has(row.instanceId) ||
        getModelDisabledReason?.(row.instanceId, row.slug) ||
        out.some((item) => item.instanceId === row.instanceId && item.slug === row.slug)
      )
        continue;
      out.push({
        slug: row.slug,
        name: row.label,
        instanceId: row.instanceId,
        driverKind: entry.driverKind,
        instanceDisplayName: entry.displayName,
      });
    }
    return out;
  }, [
    modelOptionsByInstance,
    entryByInstanceId,
    readyInstanceSet,
    groupedRoot,
    getModelDisabledReason,
  ]);

  const hasBoundEngine = props.lockedProvider !== null;
  const governedContext = props.governedContext === true;
  const isSearching = searchQuery.trim().length > 0;
  const pickerInstanceEntries = useMemo(
    () =>
      instanceEntries.filter((entry) => {
        if (!visibleInstanceSet.has(entry.instanceId)) return false;
        if (flatModels.some((model) => model.instanceId === entry.instanceId)) return true;
        if (readyInstanceSet.has(entry.instanceId)) return false;
        // A greyed provider still needs one model the World allows; a World
        // denial is not the thread's law and stays hidden as before. One that
        // is not ready may not have listed its models yet, and still shows.
        const options = modelOptionsByInstance.get(entry.instanceId) ?? [];
        return options.length === 0
          ? !isProviderInstancePickerReady(entry)
          : options.some((model) => !getModelDisabledReason?.(entry.instanceId, model.slug));
      }),
    [
      instanceEntries,
      visibleInstanceSet,
      readyInstanceSet,
      flatModels,
      modelOptionsByInstance,
      getModelDisabledReason,
    ],
  );
  // A choosable instance counts its choosable models; a greyed one shows what
  // it holds, so the row reads as a real provider the thread cannot use here.
  const modelCountOf = useCallback(
    (instanceId: ProviderInstanceId) =>
      readyInstanceSet.has(instanceId)
        ? flatModels.filter((model) => model.instanceId === instanceId).length
        : (modelOptionsByInstance.get(instanceId)?.length ?? 0),
    [flatModels, modelOptionsByInstance, readyInstanceSet],
  );
  const providerGroups = useMemo(
    () => groupModelPickerProviders(pickerInstanceEntries, modelCountOf),
    [modelCountOf, pickerInstanceEntries],
  );
  // While a query is typed the flat global results ride the models page,
  // whatever page the person was browsing; clearing it returns them there.
  const effectivePage: ModelPickerPageIndex = isSearching ? 2 : view.page;
  const crumbs = useMemo(
    () =>
      modelPickerBreadcrumb({
        view,
        root: selectedInstanceId,
        groups: providerGroups,
        searching: isSearching,
      }),
    [isSearching, providerGroups, selectedInstanceId, view],
  );
  const backTarget = useMemo(
    () => modelPickerBackTarget({ view, root: selectedInstanceId, groups: providerGroups }),
    [providerGroups, selectedInstanceId, view],
  );
  const navigateToView = useCallback(
    (target: ModelPickerView) => {
      setView(target);
      setSearchQuery("");
      window.requestAnimationFrame(() => {
        focusSearchInput();
      });
    },
    [focusSearchInput],
  );
  const handleOpenProvider = useCallback(
    (group: ModelPickerProviderGroup) => {
      const target = resolveProviderOpenTarget(group);
      if (target.kind === "models") {
        handleSelectInstance(target.instanceId);
      } else {
        navigateToView({ page: 1, providerKind: group.driverKind });
      }
    },
    [handleSelectInstance, navigateToView],
  );
  const getProviderDisabledReason = useCallback(
    (group: ModelPickerProviderGroup) => {
      // A lone account that is not ready says why first: that is what blocks
      // it everywhere. A provider with several accounts stays open, so each
      // account can show its own state on the accounts page.
      const lone = group.entries.length === 1 ? group.entries[0] : undefined;
      const loneReason = lone ? readinessReason(lone) : null;
      if (loneReason) return loneReason;
      return getGovernedProviderDisabledReason({
        governed: governedContext,
        driverKind: group.driverKind,
        label: group.label,
      });
    },
    [governedContext],
  );
  /**
   * Can 21 (2026-10-02): in a started conversation, another engine's rows are
   * choosable and say that the conversation so far goes with the next Send.
   */
  const switchNoticeFor = useCallback(
    (
      entries: ReadonlyArray<Pick<ProviderInstanceEntry, "driverKind" | "continuationGroupKey">>,
      label: string,
    ) =>
      hasBoundEngine && !entries.some((entry) => matchesBoundEngine(entry))
        ? engineSwitchNotice(label)
        : null,
    [hasBoundEngine, matchesBoundEngine],
  );
  const getProviderNote = useCallback(
    (group: ModelPickerProviderGroup) => switchNoticeFor(group.entries, group.label),
    [switchNoticeFor],
  );
  const getInstanceNote = useCallback(
    // The World-paid instance arrives named by its own id; the notice names
    // it as the product (W-41ac1e16), never the internal id.
    (entry: ProviderInstanceEntry) =>
      switchNoticeFor(
        [entry],
        entry.instanceId === MANAGED_PROVIDER_INSTANCE_ID ? "Neucleos" : entry.displayName,
      ),
    [switchNoticeFor],
  );
  const onRequestClose = props.onRequestClose;
  const router = useRouter({ warn: false });
  const [claudeVerifyProgress, setClaudeVerifyProgress] = useState<
    ReadonlyMap<ProviderInstanceId, ClaudeVerifyProgress>
  >(() => new Map());
  const canVerifyClaudeHere = Boolean(
    typeof window !== "undefined" &&
    window.desktopBridge?.connectClaudeAccount &&
    window.desktopBridge.getClaudeAccountConnection,
  );
  /** F36: the door beside a provider that is not ready — the way to fix it. */
  const recoveryDoor = useCallback(
    (entry: ProviderInstanceEntry): DrilldownDoor | null => {
      const recovery = providerInstanceRecovery(entry, { canVerifyClaudeHere });
      if (!recovery) return null;
      const openSettings =
        (section: "/settings/subscription-accounts" | "/settings/provider-keys") => () => {
          onRequestClose?.();
          openProviderSettings(section, (to) => void router?.navigate({ to }));
        };
      if (recovery.kind === "on-mac") return { kind: "recovery", label: recovery.label };
      if (recovery.kind === "open-settings") {
        return {
          kind: "recovery",
          label: recovery.label,
          onActivate: openSettings(recovery.section),
        };
      }
      const progress = claudeVerifyProgress.get(entry.instanceId);
      if (progress === "not-verified") {
        return {
          kind: "recovery",
          label: "Claude did not verify it — Open Settings",
          onActivate: openSettings("/settings/subscription-accounts"),
        };
      }
      const setProgress = (next: ClaudeVerifyProgress) =>
        setClaudeVerifyProgress((current) => new Map(current).set(entry.instanceId, next));
      return {
        kind: "recovery",
        label:
          progress === "checking"
            ? "Checking with Claude…"
            : progress === "verified"
              ? "Verified — updating…"
              : recovery.label,
        busy: progress !== undefined,
        onActivate: () => {
          if (progress !== undefined) return;
          setProgress("checking");
          void verifyNativeClaudeAccount(entry.instanceId).then(
            (outcome) => setProgress(outcome === "verified" ? "verified" : "not-verified"),
            () => setProgress("not-verified"),
          );
        },
      };
    },
    [canVerifyClaudeHere, claudeVerifyProgress, onRequestClose, router],
  );
  /**
   * W-c19cea34: a model the installed CLI is too old for is shown with why,
   * and the door to the provider's Update action in Settings.
   */
  const versionGateOf = useCallback(
    (model: ModelPickerItem): { note: string; door: DrilldownDoor } | null => {
      if (!model.needsProviderVersion) return null;
      const cliName = providerCliName(model.driverKind, model.instanceDisplayName);
      return {
        note: modelNeedsProviderVersionNote({
          cliName,
          needsVersion: model.needsProviderVersion,
          installedVersion: entryByInstanceId.get(model.instanceId)?.snapshot.version,
        }),
        door: {
          kind: "recovery",
          label: modelNeedsProviderVersionDoorLabel(cliName),
          onActivate: () => {
            onRequestClose?.();
            openProviderSettings(
              "/settings/subscription-accounts",
              (to) => void router?.navigate({ to }),
            );
          },
        },
      };
    },
    [entryByInstanceId, onRequestClose, router],
  );
  const getProviderDisabledDoor = useCallback(
    (group: ModelPickerProviderGroup) => {
      const lone = group.entries.length === 1 ? group.entries[0] : undefined;
      if (lone && !isProviderInstancePickerReady(lone)) return recoveryDoor(lone);
      return null;
    },
    [recoveryDoor],
  );
  const getInstanceDisabledDoor = useCallback(
    (entry: ProviderInstanceEntry) =>
      isProviderInstancePickerReady(entry) ? null : recoveryDoor(entry),
    [recoveryDoor],
  );
  const getInstanceDisabledReason = useCallback(
    (entry: ProviderInstanceEntry) => {
      const notReady = readinessReason(entry);
      if (notReady) return notReady;
      return getGovernedProviderDisabledReason({
        governed: governedContext,
        driverKind: entry.driverKind,
        label: entry.displayName,
      });
    },
    [governedContext],
  );
  const accountsGroup = useMemo(
    () => providerGroups.find((group) => group.driverKind === view.providerKind) ?? null,
    [providerGroups, view.providerKind],
  );
  const instanceOrder = useMemo(
    () => instanceEntries.map((entry) => entry.instanceId),
    [instanceEntries],
  );

  // Filter models based on search query and selected instance
  const filteredModels = useMemo(() => {
    let result = flatModels;

    // Apply tokenized fuzzy search across the combined provider/model search fields.
    if (searchQuery.trim()) {
      const rankedMatches = result
        .map((model) => ({
          model,
          score: scoreModelPickerSearch(
            {
              name: model.name,
              slug: model.slug,
              ...(model.shortName ? { shortName: model.shortName } : {}),
              ...(model.subProvider ? { subProvider: model.subProvider } : {}),
              driverKind: model.driverKind,
              providerDisplayName: model.instanceDisplayName,
              isFavorite: favoritesSet.has(providerModelKey(model.instanceId, model.slug)),
            },
            searchQuery,
          ),
          isFavorite: favoritesSet.has(providerModelKey(model.instanceId, model.slug)),
          tieBreaker: buildModelPickerSearchText({
            name: model.name,
            ...(model.shortName ? { shortName: model.shortName } : {}),
            ...(model.subProvider ? { subProvider: model.subProvider } : {}),
            driverKind: model.driverKind,
            providerDisplayName: model.instanceDisplayName,
          }),
        }))
        .filter(
          (
            rankedModel,
          ): rankedModel is {
            model: ModelPickerItem;
            score: number;
            isFavorite: boolean;
            tieBreaker: string;
          } => rankedModel.score !== null,
        );

      // When searching, the sidebar selection is ignored so account-scoped
      // searches can find a model before the user chooses an instance rail
      // item. Every engine is searched (Can 21, 2026-10-02: no lock filter).
      return rankedMatches
        .toSorted((a, b) => {
          const scoreDelta = a.score - b.score;
          if (scoreDelta !== 0) {
            return scoreDelta;
          }
          if (a.isFavorite !== b.isFavorite) {
            return a.isFavorite ? -1 : 1;
          }
          return a.tieBreaker.localeCompare(b.tieBreaker);
        })
        .map((rankedModel) => rankedModel.model);
    }

    if (selectedInstanceId === "favorites") {
      result = result.filter((m) => favoritesSet.has(providerModelKey(m.instanceId, m.slug)));
    } else {
      result = result.filter((m) => m.instanceId === selectedInstanceId);
    }

    return sortProviderModelItems(result, {
      favoriteModelKeys: favoritesSet,
      groupFavorites: selectedInstanceId !== "favorites",
      instanceOrder: selectedInstanceId === "favorites" ? instanceOrder : [],
    });
  }, [favoritesSet, flatModels, instanceOrder, searchQuery, selectedInstanceId]);

  const legacySection = useMemo(() => {
    if (isSearching || selectedInstanceId === "favorites") {
      return null;
    }
    const currentModels = filteredModels.filter((model) => !model.isLegacy);
    const legacyModels = filteredModels.filter((model) => model.isLegacy);
    if (legacyModels.length === 0) {
      return null;
    }
    return {
      key: modelPickerLegacySectionKey(selectedInstanceId),
      currentModels,
      legacyModels,
      isExpanded: expandedLegacyInstances.has(selectedInstanceId),
    };
  }, [expandedLegacyInstances, filteredModels, isSearching, selectedInstanceId]);

  const visibleModels = useMemo(() => {
    if (!legacySection) {
      return filteredModels;
    }
    return [
      ...legacySection.currentModels,
      ...(legacySection.isExpanded ? legacySection.legacyModels : []),
    ];
  }, [filteredModels, legacySection]);

  const toggleLegacySection = useCallback((instanceId: ProviderInstanceId) => {
    setExpandedLegacyInstances((expanded) => {
      const next = new Set(expanded);
      if (next.has(instanceId)) {
        next.delete(instanceId);
      } else {
        next.add(instanceId);
      }
      return next;
    });
  }, []);

  const handleModelSelect = useCallback(
    (modelSlug: string, instanceId: ProviderInstanceId) => {
      if (getModelDisabledReason?.(instanceId, modelSlug)) {
        return;
      }
      const options = modelOptionsByInstance.get(instanceId);
      if (!options) {
        return;
      }
      // Listed so a person can find it; it runs once the provider is updated.
      if (options.some((option) => option.slug === modelSlug && option.needsProviderVersion)) {
        return;
      }
      const entry = entryByInstanceId.get(instanceId);
      if (!entry || !isProviderInstancePickerReady(entry) || getInstanceDisabledReason(entry)) {
        return;
      }
      if (
        instanceId === "neucleos" &&
        ![...groupedRoot.neucleos, ...groupedRoot.frontier].some((row) => row.slug === modelSlug)
      )
        return;
      // `resolveSelectableModel` uses the driver kind for normalization
      // (slug casing etc.). Custom instances share their driver's
      // normalization rules, so pass the driver kind here.
      const resolvedModel =
        entry.snapshot.modelCatalogSource === "world-managed"
          ? modelSlug
          : resolveSelectableModel(entry.driverKind, modelSlug, options);
      if (resolvedModel) {
        onInstanceModelChange(instanceId, resolvedModel);
      }
    },
    [
      entryByInstanceId,
      getModelDisabledReason,
      getInstanceDisabledReason,
      groupedRoot,
      modelOptionsByInstance,
      onInstanceModelChange,
    ],
  );

  const toggleFavorite = useCallback(
    (instanceId: ProviderInstanceId, model: string) => {
      const newFavorites = [...favorites];
      const index = newFavorites.findIndex((f) => f.provider === instanceId && f.model === model);
      if (index >= 0) {
        newFavorites.splice(index, 1);
      } else {
        newFavorites.push({ provider: instanceId, model });
      }
      updateSettings({ favorites: newFavorites });
    },
    [favorites, updateSettings],
  );

  const modelJumpCommandByKey = useMemo(() => {
    const mapping = new Map<
      string,
      NonNullable<ReturnType<typeof modelPickerJumpCommandForIndex>>
    >();
    let selectableModelIndex = 0;
    for (const model of visibleModels) {
      if (model.needsProviderVersion || getModelDisabledReason?.(model.instanceId, model.slug)) {
        continue;
      }
      const jumpCommand = modelPickerJumpCommandForIndex(selectableModelIndex);
      if (!jumpCommand) {
        return mapping;
      }
      mapping.set(modelPickerModelKey(model.instanceId, model.slug), jumpCommand);
      selectableModelIndex += 1;
    }
    return mapping;
  }, [getModelDisabledReason, visibleModels]);
  const modelJumpModelKeys = useMemo(
    () => [...modelJumpCommandByKey.keys()],
    [modelJumpCommandByKey],
  );
  const allItemKeys = useMemo(
    (): string[] => [
      ...flatModels.map((model) => modelPickerModelKey(model.instanceId, model.slug)),
      ...new Set(
        flatModels
          .filter((model) => model.isLegacy)
          .map((model) => modelPickerLegacySectionKey(model.instanceId)),
      ),
    ],
    [flatModels],
  );
  const filteredItemKeys = useMemo((): string[] => {
    const modelKeys = visibleModels.map((model) =>
      modelPickerModelKey(model.instanceId, model.slug),
    );
    if (!legacySection) {
      return modelKeys;
    }
    modelKeys.splice(legacySection.currentModels.length, 0, legacySection.key);
    return modelKeys;
  }, [legacySection, visibleModels]);
  const filteredModelByKey = useMemo(
    (): ReadonlyMap<string, ModelPickerItem> =>
      new Map(
        visibleModels.map(
          (model) => [modelPickerModelKey(model.instanceId, model.slug), model] as const,
        ),
      ),
    [visibleModels],
  );
  const updateModelListScrollFades = useCallback(() => {
    const scrollElement = modelListRef.current?.getScrollableNode();
    if (!(scrollElement instanceof HTMLElement)) {
      return;
    }
    const maxScrollOffset = Math.max(0, scrollElement.scrollHeight - scrollElement.clientHeight);
    setShowTopScrollFade(scrollElement.scrollTop > 1);
    setShowBottomScrollFade(maxScrollOffset - scrollElement.scrollTop > 1);
  }, []);
  const getModelListScrollElement = useCallback(() => {
    const scrollElement = modelListRef.current?.getScrollableNode();
    return scrollElement instanceof HTMLElement ? scrollElement : null;
  }, []);
  const { syncReveal } = useModelListReveal({
    getScrollElement: getModelListScrollElement,
    contextKey: `${String(selectedInstanceId)}|${isSearching ? "search" : "browse"}`,
    itemsKey: filteredItemKeys,
  });
  const handleModelListScroll = useCallback(() => {
    updateModelListScrollFades();
    syncReveal();
  }, [syncReveal, updateModelListScrollFades]);
  const modelJumpShortcutContext = useMemo(
    () =>
      ({
        terminalFocus: false,
        terminalOpen: props.terminalOpen,
        modelPickerOpen: true,
      }) as const,
    [props.terminalOpen],
  );
  const modelJumpLabelByKey = useMemo((): ReadonlyMap<string, string> => {
    if (modelJumpCommandByKey.size === 0) {
      return EMPTY_MODEL_JUMP_LABELS;
    }
    const shortcutLabelOptions = {
      platform: navigator.platform,
      context: modelJumpShortcutContext,
    };
    const mapping = new Map<string, string>();
    for (const [modelKey, command] of modelJumpCommandByKey) {
      const label = shortcutLabelForCommand(keybindings, command, shortcutLabelOptions);
      if (label) {
        mapping.set(modelKey, label);
      }
    }
    return mapping.size > 0 ? mapping : EMPTY_MODEL_JUMP_LABELS;
  }, [keybindings, modelJumpCommandByKey, modelJumpShortcutContext]);
  // W-a8c32a52: in a conversation holding images, a managed model the World
  // says cannot read them carries a visible mark on every row that offers
  // it: the root chooser and the model list its search fills.
  const conversationImageCount = props.conversationImageCount ?? 0;
  const managedCatalog = props.managedCatalog ?? null;
  const imageNoteFor = useCallback(
    (instanceId: ProviderInstanceId, slug: string): string | null =>
      conversationImageCount > 0 &&
      instanceId === "neucleos" &&
      managedSlugReadsImages(managedCatalog, slug) === false
        ? MANAGED_IMAGES_UNREADABLE
        : null,
    [conversationImageCount, managedCatalog],
  );
  const modelListExtraData = useMemo(
    () => ({ favoritesSet, modelJumpLabelByKey, imageNoteFor, versionGateOf }),
    [favoritesSet, modelJumpLabelByKey, imageNoteFor, versionGateOf],
  );

  useEffect(() => {
    const onWindowKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) {
        return;
      }
      if (effectivePage !== 2) {
        // Jump labels ride the visible model list; the providers and
        // accounts pages have no rows the numbers could honestly name.
        return;
      }

      const command = resolveShortcutCommand(event, keybindings, {
        platform: navigator.platform,
        context: modelJumpShortcutContext,
      });
      const jumpIndex = modelPickerJumpIndexFromCommand(command ?? "");
      if (jumpIndex === null) {
        return;
      }

      const targetModelKey = modelJumpModelKeys[jumpIndex];
      if (!targetModelKey) {
        return;
      }
      const model = parseModelPickerModelKey(targetModelKey);
      if (!model) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      handleModelSelect(model.slug, model.instanceId);
    };

    window.addEventListener("keydown", onWindowKeyDown, true);

    return () => {
      window.removeEventListener("keydown", onWindowKeyDown, true);
    };
  }, [effectivePage, handleModelSelect, keybindings, modelJumpModelKeys, modelJumpShortcutContext]);

  useLayoutEffect(() => {
    setShowTopScrollFade(false);
    setShowBottomScrollFade(filteredItemKeys.length > 5);
    let nestedFrame = 0;
    const frame = window.requestAnimationFrame(() => {
      updateModelListScrollFades();
      nestedFrame = window.requestAnimationFrame(updateModelListScrollFades);
    });
    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(nestedFrame);
    };
  }, [filteredItemKeys, updateModelListScrollFades]);

  const renderManagedRow = (row: ManagedChooserRow) => {
    const entry = entryByInstanceId.get(row.instanceId);
    // A model this World withholds stays hidden (239d378b). A choice only this
    // conversation cannot take (it runs on another engine family, PKR1) stays
    // visible, greyed, with its reason, instead of silently leaving the section.
    if (!entry || getModelDisabledReason?.(row.instanceId, row.slug)) return null;
    const reason = getInstanceDisabledReason(entry);
    const imageNote = imageNoteFor(row.instanceId, row.slug);
    const switchNotice = reason ? null : getInstanceNote(entry);
    const note = [reason ?? row.readiness, switchNotice, imageNote].filter(Boolean).join(" · ");
    return (
      <button
        type="button"
        key={row.slug}
        {...(imageNote ? { "data-model-cannot-read-images": "true" } : {})}
        aria-disabled={Boolean(reason)}
        title={reason ?? undefined}
        className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-muted aria-disabled:opacity-50"
        onClick={() => {
          if (!reason) handleModelSelect(row.slug, row.instanceId);
        }}
      >
        <span>
          {row.label}
          {note && <span className="mt-0.5 block text-muted-foreground">{note}</span>}
        </span>
        <span className="text-muted-foreground">{row.cost}</span>
      </button>
    );
  };
  const frontierLabs = [...new Set(groupedRoot.frontier.map((row) => row.slug.split("/")[0]!))];
  // An empty managed section says why: the World's refusal, the catalog still
  // being read (never "unavailable" while it is), or the chooser's own reason.
  const managedNote = props.managedCatalogRefusal
    ? { kind: "refusal", text: props.managedCatalogRefusal }
    : props.managedCatalogLoading && !props.managedCatalog
      ? { kind: "loading", text: MANAGED_CATALOG_LOADING }
      : { kind: "reason", text: groupedRoot.reason };

  return (
    <TooltipProvider delay={0}>
      <div
        className="dropdown-glass relative flex h-screen max-h-94 w-screen max-w-90 flex-col overflow-hidden rounded-lg text-popover-foreground shadow-[0_16px_40px_-18px_rgb(0_0_0/55%)] [clip-path:inset(0_round_var(--radius-lg))] dark:shadow-[0_18px_44px_-18px_rgb(0_0_0/80%)]"
        data-model-picker-content="true"
      >
        <Combobox
          inline
          items={allItemKeys}
          filteredItems={effectivePage === 2 ? filteredItemKeys : EMPTY_ITEM_KEYS}
          filter={null}
          autoHighlight
          open
          virtualized
          value={modelPickerModelKey(props.activeInstanceId, props.model)}
          onItemHighlighted={(modelKey, eventDetails) => {
            highlightedModelKeyRef.current = typeof modelKey === "string" ? modelKey : null;
            if (eventDetails.reason === "keyboard" && eventDetails.index >= 0) {
              void modelListRef.current?.scrollIndexIntoView?.({
                index: eventDetails.index,
                animated: false,
              });
            }
          }}
          onValueChange={(modelKey) => {
            if (typeof modelKey !== "string") {
              return;
            }
            const legacyInstanceId = parseModelPickerLegacySectionKey(modelKey);
            if (legacyInstanceId) {
              toggleLegacySection(legacyInstanceId);
              return;
            }
            const model = parseModelPickerModelKey(modelKey);
            if (model) {
              handleModelSelect(model.slug, model.instanceId);
            }
          }}
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-muted/40">
            {/* Breadcrumb rail — drill-up targets are modelPickerBreadcrumb law */}
            <div className="flex min-w-0 items-center gap-0.5 px-2 pt-2">
              <button
                type="button"
                aria-label="Back"
                disabled={!isSearching && backTarget === null}
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-[color,opacity] hover:text-foreground focus-visible:text-foreground focus-visible:outline-none",
                  !isSearching && backTarget === null && "pointer-events-none opacity-30",
                )}
                onClick={() => {
                  if (isSearching) {
                    setSearchQuery("");
                    window.requestAnimationFrame(() => {
                      focusSearchInput();
                    });
                    return;
                  }
                  if (backTarget) {
                    navigateToView(backTarget);
                  }
                }}
              >
                <ChevronLeftIcon className="size-4" />
              </button>
              <nav
                aria-label="Model picker path"
                className="flex min-w-0 flex-1 items-center gap-0.5 text-xs leading-snug"
              >
                {crumbs.map((crumb, crumbIndex) => {
                  const target = crumb.target;
                  return (
                    <Fragment key={crumb.key}>
                      {crumbIndex > 0 ? (
                        <span aria-hidden className="shrink-0 text-muted-foreground/50">
                          /
                        </span>
                      ) : null}
                      {target ? (
                        <button
                          type="button"
                          className="min-w-0 truncate rounded px-1 py-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none"
                          onClick={() => navigateToView(target)}
                        >
                          {crumb.label}
                        </button>
                      ) : (
                        <span className="min-w-0 truncate px-1 py-0.5 font-medium">
                          {crumb.label}
                        </span>
                      )}
                    </Fragment>
                  );
                })}
              </nav>
            </div>

            {/* Search bar */}
            <div className="px-2 pt-2">
              <div className="border-b border-border/70 pb-2.5 transition-colors focus-within:border-ring">
                <ComboboxInput
                  ref={searchInputRef}
                  className="[&_input]:h-6.5 [&_input]:font-sans [&_input]:leading-6.5"
                  inputClassName="rounded-none bg-transparent text-sm"
                  placeholder="Search models..."
                  showTrigger={false}
                  startAddon={
                    <SearchIcon className="-translate-x-0.5 size-4 shrink-0 text-muted-foreground opacity-70" />
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      e.stopPropagation();
                      props.onRequestClose?.();
                      return;
                    }
                    if (e.key === "Enter" && highlightedModelKeyRef.current) {
                      (
                        e as typeof e & { preventBaseUIHandler?: () => void }
                      ).preventBaseUIHandler?.();
                      e.preventDefault();
                      e.stopPropagation();
                      const legacyInstanceId = parseModelPickerLegacySectionKey(
                        highlightedModelKeyRef.current,
                      );
                      if (legacyInstanceId) {
                        toggleLegacySection(legacyInstanceId);
                        return;
                      }
                      const model = parseModelPickerModelKey(highlightedModelKeyRef.current);
                      if (model) {
                        handleModelSelect(model.slug, model.instanceId);
                      }
                      return;
                    }
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  size="sm"
                  unstyled
                />
              </div>
            </div>

            {/* Pages: providers → accounts → models. Positions come from
                modelPickerPageState — the N-page generalization of the
                transitions-dev 08 side-by-side recipe. */}
            <div className="relative min-h-0 flex-1 overflow-hidden">
              <div
                className="mp-page"
                data-page-state={modelPickerPageState(0, effectivePage)}
                inert={effectivePage !== 0}
              >
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-2 py-2">
                  {flatModels.find(
                    (item) =>
                      item.instanceId === props.activeInstanceId && item.slug === props.model,
                  ) && (
                    <section data-model-chooser-group="recent">
                      <h3 className="px-2 text-xs text-muted-foreground">Recent</h3>
                      <button
                        type="button"
                        className="w-full rounded p-2 text-left text-xs hover:bg-muted"
                        onClick={() => handleModelSelect(props.model, props.activeInstanceId)}
                      >
                        {
                          flatModels.find(
                            (item) =>
                              item.instanceId === props.activeInstanceId &&
                              item.slug === props.model,
                          )?.name
                        }
                      </button>
                    </section>
                  )}
                  <section data-model-chooser-group="neucleos">
                    <h3 className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground">
                      <NeucleosTriangle className="size-3 text-neucleos-identity" />
                      neucleos
                    </h3>
                    {groupedRoot.neucleos.length ? (
                      groupedRoot.neucleos.map(renderManagedRow)
                    ) : (
                      <p
                        className="px-2 py-1 text-xs text-muted-foreground"
                        data-model-chooser-note={managedNote.kind}
                      >
                        {managedNote.text}
                      </p>
                    )}
                  </section>
                  <section data-model-chooser-group="frontier">
                    <h3 className="px-2 py-1 text-xs text-muted-foreground">Frontier</h3>
                    {frontierLabs.length ? (
                      frontierLabs.map((lab) => (
                        <details key={lab} className="group/lab">
                          <summary className="flex cursor-pointer list-none items-center justify-between rounded px-2 py-1.5 text-xs hover:bg-muted">
                            {{
                              openai: "OpenAI",
                              anthropic: "Anthropic",
                              google: "Google",
                              meta: "Meta",
                              xai: "xAI",
                            }[lab] ?? lab}
                            <ChevronRightIcon className="size-3 group-open/lab:rotate-90" />
                          </summary>
                          <div className="pl-2">
                            {groupedRoot.frontier
                              .filter((row) => row.slug.startsWith(`${lab}/`))
                              .map(renderManagedRow)}
                          </div>
                        </details>
                      ))
                    ) : (
                      <p
                        className="px-2 py-1 text-xs text-muted-foreground"
                        data-model-chooser-note={managedNote.kind}
                      >
                        {managedNote.text}
                      </p>
                    )}
                  </section>
                  {groupedRoot.onThisMac.length ? (
                    <section data-model-chooser-group="on-this-mac">
                      <h3 className="px-2 py-1 text-xs text-muted-foreground">On this Mac</h3>
                      {groupedRoot.onThisMac.map(renderManagedRow)}
                    </section>
                  ) : null}
                  <section data-model-chooser-group="subscriptions">
                    <h3 className="px-2 py-1 text-xs text-muted-foreground">Your subscriptions</h3>
                    <ModelPickerProvidersPage
                      groups={providerGroups.filter((group) => group.driverKind !== "aiSdk")}
                      favoritesCount={favorites.length}
                      onOpenFavorites={() => handleSelectInstance("favorites")}
                      onOpenProvider={handleOpenProvider}
                      getProviderDisabledReason={getProviderDisabledReason}
                      getProviderDisabledDoor={getProviderDisabledDoor}
                      getProviderNote={getProviderNote}
                    />
                  </section>
                  {pickerInstanceEntries.some(
                    (entry) =>
                      entry.driverKind === "aiSdk" && Boolean(entry.snapshot.connectionWorldKey),
                  ) ? (
                    <section data-model-chooser-group="personal-api">
                      <h3 className="px-2 py-1 text-xs text-muted-foreground">
                        Your API connections
                      </h3>
                      {pickerInstanceEntries
                        .filter(
                          (entry) =>
                            entry.driverKind === "aiSdk" &&
                            Boolean(entry.snapshot.connectionWorldKey),
                        )
                        .map((entry) => {
                          const disabledReason = getInstanceDisabledReason(entry);
                          const door = disabledReason ? getInstanceDisabledDoor(entry) : null;
                          const switchNotice = disabledReason ? null : getInstanceNote(entry);
                          return (
                            <Fragment key={entry.instanceId}>
                              <button
                                type="button"
                                className={cn(
                                  "w-full rounded p-2 text-left text-xs hover:bg-muted",
                                  disabledReason &&
                                    "cursor-not-allowed opacity-50 hover:bg-transparent",
                                )}
                                aria-disabled={Boolean(disabledReason)}
                                onClick={() => {
                                  if (!disabledReason) handleSelectInstance(entry.instanceId);
                                }}
                              >
                                {entry.displayName}
                                <span className="ml-2 text-muted-foreground">
                                  {modelCountOf(entry.instanceId)} models
                                </span>
                                {disabledReason ? (
                                  <span className="block text-muted-foreground">
                                    {disabledReason}
                                  </span>
                                ) : null}
                                {switchNotice ? (
                                  <span
                                    className="block text-muted-foreground"
                                    data-model-picker-switch-notice=""
                                  >
                                    {switchNotice}
                                  </span>
                                ) : null}
                              </button>
                              {door ? <DrilldownDoorControl door={door} className="ml-2" /> : null}
                            </Fragment>
                          );
                        })}
                    </section>
                  ) : null}
                </div>
              </div>
              <div
                className="mp-page"
                data-page-state={modelPickerPageState(1, effectivePage)}
                inert={effectivePage !== 1}
              >
                <ModelPickerAccountsPage
                  group={accountsGroup}
                  selectedInstanceId={props.activeInstanceId}
                  modelCountOf={modelCountOf}
                  onSelectInstance={handleSelectInstance}
                  getInstanceDisabledReason={getInstanceDisabledReason}
                  getInstanceDisabledDoor={getInstanceDisabledDoor}
                  getInstanceNote={getInstanceNote}
                />
              </div>
              <div
                className="mp-page"
                data-page-state={modelPickerPageState(2, effectivePage)}
                inert={effectivePage !== 2}
              >
                {/* Model list */}
                <div className="relative min-h-0 flex-1 overflow-hidden pr-px">
                  <ComboboxListVirtualized className="size-full min-w-0 p-0 not-empty:p-0">
                    <LegendList<string>
                      ref={modelListRef}
                      data={filteredItemKeys}
                      extraData={modelListExtraData}
                      keyExtractor={(modelKey) => modelKey}
                      renderItem={({ item: modelKey, index }) => {
                        // Reveal wrapper: keyed hidden-until-scrolled state,
                        // resolved against recycled nodes in useModelListReveal.
                        const revealWrap = (row: React.ReactNode) => (
                          <div className="mp-reveal" data-reveal-key={modelKey}>
                            {row}
                          </div>
                        );
                        if (legacySection?.key === modelKey) {
                          return revealWrap(
                            <ComboboxItem
                              hideIndicator
                              index={index}
                              value={modelKey}
                              aria-expanded={legacySection.isExpanded}
                              className="group w-full cursor-pointer rounded-md px-2 py-2"
                              contentClassName="flex w-full items-center gap-3"
                            >
                              <div className="min-w-0 flex-1 text-left">
                                <div className="text-xs font-medium leading-snug">
                                  Legacy models
                                </div>
                                <div className="mt-1 text-xs font-normal leading-snug text-muted-foreground/70">
                                  {legacySection.legacyModels.length} models
                                </div>
                              </div>
                              <ChevronRightIcon
                                className={cn(
                                  "size-4 transition-transform",
                                  legacySection.isExpanded && "rotate-90",
                                )}
                              />
                            </ComboboxItem>,
                          );
                        }
                        const model = filteredModelByKey.get(modelKey);
                        if (!model) {
                          return null;
                        }
                        const threadReason =
                          getModelDisabledReason?.(model.instanceId, model.slug) ?? null;
                        const versionGate = threadReason ? null : versionGateOf(model);
                        const disabledReason = threadReason ?? versionGate?.note ?? null;
                        return revealWrap(
                          <>
                            <ModelListRow
                              key={modelKey}
                              index={index}
                              model={model}
                              instanceId={model.instanceId}
                              driverKind={model.driverKind}
                              providerDisplayName={model.instanceDisplayName}
                              providerAccentColor={model.instanceAccentColor}
                              isFavorite={favoritesSet.has(
                                providerModelKey(model.instanceId, model.slug),
                              )}
                              isSelected={
                                modelKey ===
                                modelPickerModelKey(props.activeInstanceId, props.model)
                              }
                              showProvider
                              preferShortName={!hasBoundEngine}
                              useTriggerLabel={false}
                              showNewBadge={isModelPickerNewModel(model.driverKind, model.slug)}
                              jumpLabel={modelJumpLabelByKey.get(modelKey) ?? null}
                              disabledReason={disabledReason}
                              showDisabledReason={versionGate !== null}
                              note={versionGate ? null : imageNoteFor(model.instanceId, model.slug)}
                              onToggleFavorite={() => toggleFavorite(model.instanceId, model.slug)}
                            />
                            {versionGate ? (
                              <DrilldownDoorControl door={versionGate.door} className="ml-0" />
                            ) : null}
                          </>,
                        );
                      }}
                      estimatedItemSize={52}
                      drawDistance={480}
                      recycleItems
                      contentContainerClassName="pl-2 pr-px"
                      ItemSeparatorComponent={ModelListSeparator}
                      onLayout={handleModelListScroll}
                      onScroll={handleModelListScroll}
                      className={cn(
                        "scrollbar-gutter-stable h-full overflow-x-hidden overscroll-y-contain py-1.5 [&::-webkit-scrollbar-track]:my-2",
                        getVirtualizedScrollFadeClassName({
                          top: showTopScrollFade,
                          bottom: showBottomScrollFade,
                        }),
                      )}
                    />
                  </ComboboxListVirtualized>
                </div>
                <ComboboxEmpty className="not-empty:py-6 empty:h-0 text-xs font-normal leading-snug">
                  No models found
                </ComboboxEmpty>
              </div>
            </div>
          </div>
        </Combobox>
        <div className="shrink-0 border-t border-border px-2 py-1.5">
          <ManageModelsButton onOpen={props.onRequestClose} />
        </div>
      </div>
    </TooltipProvider>
  );
});
